import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { createLedgerFromLease, formatCents } from './api';

/**
 * Preview + create rent ledger dialog (use from LeaseManagement).
 */
export function CreateRentLedgerDialog({
  open,
  lease,
  onClose,
  onCreated,
  onOpenLedger
}) {
  const [commitCharges, setCommitCharges] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const preview = useMemo(() => {
    if (!lease) return null;
    const rent = Number(lease.monthlyRent) || 0;
    const deposit = Number(lease.securityDeposit) || 0;
    const pet = Number(lease.petDeposit) || 0;
    return { rent, deposit, pet };
  }, [lease]);

  const handleCreate = async () => {
    if (!lease?.id) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await createLedgerFromLease(lease.id, { commitCharges });
      setSnackbar({
        open: true,
        message: `Ledger created · ${result.rentScheduleCount} schedule lines`
      });
      if (typeof onCreated === 'function') onCreated(result);
      if (typeof onOpenLedger === 'function') {
        onOpenLedger({
          tenantId: lease.tenantId,
          propertyId: lease.propertyId,
          unitId: lease.unitId,
          leaseId: lease.id,
          tenantLabel: lease.tenantName
        });
      }
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.message || 'Failed to create ledger';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={() => !submitting && onClose()} fullWidth maxWidth="sm">
        <DialogTitle>Create Rent Ledger</DialogTitle>
        <DialogContent>
          {lease && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {lease.propertyName || lease.propertyId} ·{' '}
                {lease.tenantName || lease.tenantId}
              </Typography>
              <Typography variant="body2" paragraph>
                This creates monthly rent schedule lines
                {preview?.deposit || preview?.pet
                  ? ' and deposit records'
                  : ''}{' '}
                from the lease dates.
              </Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Est.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Monthly rent</TableCell>
                    <TableCell align="right">
                      {formatCents(Math.round((preview?.rent || 0) * 100))}
                    </TableCell>
                  </TableRow>
                  {preview?.deposit > 0 && (
                    <TableRow>
                      <TableCell>Security deposit</TableCell>
                      <TableCell align="right">
                        {formatCents(Math.round(preview.deposit * 100))}
                      </TableCell>
                    </TableRow>
                  )}
                  {preview?.pet > 0 && (
                    <TableRow>
                      <TableCell>Pet deposit</TableCell>
                      <TableCell align="right">
                        {formatCents(Math.round(preview.pet * 100))}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={commitCharges}
                    onChange={(e) => setCommitCharges(e.target.checked)}
                  />
                }
                label="Post due or past rent months as charges now"
              />
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={submitting || !lease?.id}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting ? 'Creating…' : 'Create Ledger'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </>
  );
}

export default CreateRentLedgerDialog;
